import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import AppleLogin from "react-apple-login";
import { jwtDecode } from "jwt-decode";
import {
  Paper,
  TextInput,
  PasswordInput,
  Button,
  Title,
  Container,
  Text,
  Alert,
  Stack,
  Divider,
  Center,
  Group,
  Box,
  Anchor,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconBuildingStore,
  IconInfoCircle,
} from "@tabler/icons-react";
import { WEB_BASE_URL, appleRedirectUri } from "../../config/baseUrls";

// OFFICIAL GOOGLE SVG LOGO
const GoogleIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

// OFFICIAL APPLE SVG LOGO
const AppleIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

export default function ShopOwnerLogin() {
  const navigate = useNavigate();
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);

  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState(false);
  const [authProvider, setAuthProvider] = useState(null);

  const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID;
  const REDIRECT_URI = appleRedirectUri(`${WEB_BASE_URL}/shop-owner`);
  const BACKEND_URL =
    process.env.REACT_APP_BACKEND_URL ||
    "https://ota-backend.altariumconsulting.com";

  useEffect(() => {
    const token = localStorage.getItem("shopToken");
    if (token) navigate("/shop-owner/shops");
  }, [navigate]);

  const handleAuthSuccess = (data) => {
    localStorage.setItem("shopToken", data.token);
    localStorage.setItem("shopUser", JSON.stringify(data.user));

    if (data.user.hasCompletedOnboarding) {
      navigate("/shop-owner/shops");
    } else {
      navigate("/shop-owner/onboarding");
    }
  };

  const handleStandardLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setAuthProvider("email");
    setError(null);

    try {
      const res = await fetch(`${BACKEND_URL}/shop-owner/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();

      if (data.success) {
        handleAuthSuccess(data);
      } else {
        setError(data.error || "Invalid credentials");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
      setAuthProvider(null);
    }
  };

  const processSocialAuth = async (payload) => {
    try {
      const res = await fetch(`${BACKEND_URL}/shop-owner/social-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      if (data.success) {
        handleAuthSuccess(data);
      } else {
        setError(data.error || `${payload.provider} authentication failed.`);
      }
    } catch (err) {
      console.error(err);
      setError(`Failed to process ${payload.provider} login.`);
    } finally {
      setSocialLoading(false);
      setAuthProvider(null);
    }
  };

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setSocialLoading(true);
      setAuthProvider("google");
      setError(null);
      try {
        const res = await fetch(
          "https://www.googleapis.com/oauth2/v3/userinfo",
          {
            headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
          },
        );
        const userInfo = await res.json();

        await processSocialAuth({
          email: userInfo.email,
          nickname: userInfo.name || "Merchant",
          provider: "google",
          providerId: userInfo.sub,
        });
      } catch (error) {
        setError("Failed to securely fetch Google profile data.");
        setSocialLoading(false);
        setAuthProvider(null);
      }
    },
    onError: () => {
      setError("Google login popup closed or failed.");
      setSocialLoading(false);
      setAuthProvider(null);
    },
  });

  // Reusable component for the new "Card-based" social buttons
  const SocialAuthCard = ({ icon: Icon, label, onClick, loadingProvider }) => (
    <Paper
      withBorder
      p="md"
      radius="md"
      onClick={onClick}
      style={{
        cursor: "pointer",
        transition: "all 0.2s ease",
        borderColor: "var(--mantine-color-gray-3)",
        opacity: socialLoading && authProvider !== loadingProvider ? 0.6 : 1,
        pointerEvents: socialLoading ? "none" : "auto",
      }}
      className="hover:shadow-md hover:bg-gray-50"
    >
      <Group justify="center" gap="md">
        <Icon />
        <Text size="lg" fw={600}>
          {socialLoading && authProvider === loadingProvider
            ? "Connecting..."
            : label}
        </Text>
      </Group>
    </Paper>
  );

  return (
    <Container size="xs" px="md" my={{ base: 40, sm: 80 }}>
      <Stack align="center" mb="xl" gap={8}>
        <IconBuildingStore
          size={64}
          style={{
            color: "var(--mantine-color-blue-6)",
            filter: "drop-shadow(0px 4px 10px rgba(59, 130, 246, 0.2))",
          }}
        />
        <Title
          order={1}
          ta="center"
          weight={900}
          tracking={-0.5}
          style={{ letterSpacing: "-0.03em" }}
          className="ota-gradient-text"
        >
          Merchant Hub
        </Title>
        <Text c="dimmed" size="lg" ta="center" px="sm">
          {isSignUp
            ? "Create your account to start managing your live inventory."
            : "Sign in to manage your physical stall and real-time orders."}
        </Text>
      </Stack>

      <Paper
        withBorder
        shadow="xl"
        p={{ base: 20, sm: 30 }}
        radius="lg"
        style={{
          border: "1px solid var(--mantine-color-gray-3)",
          backgroundColor: "var(--mantine-color-white)",
        }}
      >
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            mb="lg"
            radius="md"
          >
            {error}
          </Alert>
        )}

        {isSignUp ? (
          /* =========================================
             SIGN UP FLOW
             ========================================= */
          <Stack gap="lg">
            <Text size="md" fw={600} ta="center" mb="xs">
              Choose a provider to register instantly
            </Text>

            <SocialAuthCard
              icon={GoogleIcon}
              label="Sign up with Google"
              loadingProvider="google"
              onClick={() => handleGoogleLogin()}
            />

            <AppleLogin
              clientId={APPLE_CLIENT_ID}
              redirectURI={REDIRECT_URI}
              scope="email name"
              responseMode="form_post"
              usePopup={true}
              callback={(res) => {
                if (res.authorization?.id_token) {
                  setSocialLoading(true);
                  setAuthProvider("apple");
                  const decoded = jwtDecode(res.authorization.id_token);
                  processSocialAuth({
                    email: decoded.email,
                    nickname: decoded.name || "Merchant",
                    provider: "apple",
                    providerId: decoded.sub,
                  });
                } else {
                  setError("Apple login failed or was cancelled.");
                }
              }}
              render={(props) => (
                <SocialAuthCard
                  icon={AppleIcon}
                  label="Sign up with Apple"
                  loadingProvider="apple"
                  onClick={props.onClick}
                />
              )}
            />

            {/* Informational Banner for Manual Accounts */}
            <Paper
              withBorder
              p="md"
              radius="md"
              bg="blue.0"
              mt="sm"
              style={{ borderColor: "var(--mantine-color-blue-2)" }}
            >
              <Group align="flex-start" wrap="nowrap" gap="sm">
                <IconInfoCircle
                  size={24}
                  color="var(--mantine-color-blue-6)"
                  style={{ marginTop: "2px" }}
                />
                <Box>
                  <Text size="sm" fw={700} c="blue.9" mb={4}>
                    Need a manual email account?
                  </Text>
                  <Text size="sm" c="blue.8">
                    Manual email and password credentials are securely
                    distributed directly by the OTA Tech Team. If you don't have
                    one, please use a social login above to register.
                  </Text>
                </Box>
              </Group>
            </Paper>
          </Stack>
        ) : (
          /* =========================================
             SIGN IN FLOW
             ========================================= */
          <Stack gap="lg">
            <SocialAuthCard
              icon={GoogleIcon}
              label="Sign in with Google"
              loadingProvider="google"
              onClick={() => handleGoogleLogin()}
            />

            <AppleLogin
              clientId={APPLE_CLIENT_ID}
              redirectURI={REDIRECT_URI}
              scope="email name"
              responseMode="form_post"
              usePopup={true}
              callback={(res) => {
                if (res.authorization?.id_token) {
                  setSocialLoading(true);
                  setAuthProvider("apple");
                  const decoded = jwtDecode(res.authorization.id_token);
                  processSocialAuth({
                    email: decoded.email,
                    nickname: decoded.name || "Merchant",
                    provider: "apple",
                    providerId: decoded.sub,
                  });
                } else {
                  setError("Apple login failed or was cancelled.");
                }
              }}
              render={(props) => (
                <SocialAuthCard
                  icon={AppleIcon}
                  label="Sign in with Apple"
                  loadingProvider="apple"
                  onClick={props.onClick}
                />
              )}
            />

            <Divider
              label="Or use manual credentials"
              labelPosition="center"
              my="xs"
              styles={{
                label: {
                  color: "var(--mantine-color-gray-5)",
                  fontSize: "12px",
                  fontWeight: 600,
                },
              }}
            />

            <form onSubmit={handleStandardLogin}>
              <Stack gap="md">
                <TextInput
                  label="Email Address"
                  placeholder="Example: name@store.com"
                  description="Use the email provided by the OTA team or your registered account."
                  required
                  size="lg"
                  radius="md"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <PasswordInput
                  label="Password"
                  placeholder="Enter your secure password"
                  required
                  size="lg"
                  radius="md"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />

                <Button
                  fullWidth
                  size="xl"
                  radius="md"
                  type="submit"
                  loading={loading && authProvider === "email"}
                  color="blue"
                  mt="sm"
                  style={{ boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)" }}
                >
                  Sign In to Workspace
                </Button>
              </Stack>
            </form>
          </Stack>
        )}

        {/* Toggle Between Sign In / Sign Up */}
        <Center
          mt="xl"
          pt="md"
          style={{ borderTop: "1px solid var(--mantine-color-gray-2)" }}
        >
          <Text size="lg" c="dimmed">
            {isSignUp ? "Already have a store? " : "New to the platform? "}
            <Anchor
              fw={700}
              c="blue.6"
              onClick={() => {
                setIsSignUp(!isSignUp);
                setError(null);
              }}
              style={{ cursor: "pointer" }}
            >
              {isSignUp ? "Sign In Here" : "Register Now"}
            </Anchor>
          </Text>
        </Center>
      </Paper>
    </Container>
  );
}
